import React, {useMemo, useState} from 'react';
import {Checkbox} from '@mui/material';
import {SVG} from '../../constants/assetLists';
import {highlightSearchMatch} from '../../pages/operations/components/AnnouncementsAndTasks/components/AnnouncementCard';
import TablePaginationActions from './AppTablePagination';

type SortOrder = 'asc' | 'desc';
export type SortRule = {field: string; order: SortOrder};

type Accessor<T> = keyof T | ((row: T) => any);

export type ColumnDef<T> = {
  /** Column title */
  title: string;
  /** Field key or accessor fn */
  accessor: Accessor<T>;
  /** Unique field id for sorting */
  field: string;
  /** Enable sorting for this column */
  sortable?: boolean;
  /** Optional custom cell renderer */
  render?: (row: T) => React.ReactNode;
  /** Optional class names for <td> */
  cellClassName?: string | ((row: T) => string);
  /** Optional class names for <th> */
  headerClassName?: string;
  /** Optional fixed width in pixels */
  width?: number;
  /** Enable search highlighting for this column (default: true) */
  searchable?: boolean;
};

export type ReusableTableProps<T> = {
  data: T[];
  columns: ColumnDef<T>[];
  rowKey: (row: T) => string | number;

  /** Selection (optional) */
  selectable?: boolean;
  selectedKeys?: Array<string | number>;
  onSelectionChange?: (keys: Array<any>) => void;
  rowDisabled?: (row: T) => boolean;
  searchText?: string;
  /** Sorting */
  multiSort?: boolean; // default false
  initialSort?: SortRule[];
  onSortChange?: (sort: SortRule[]) => void;

  /** Row className (e.g., disabled rows) */
  rowClassName?: (row: T) => string;
  /** Fired when a row is clicked */
  onRowClick?: (row: T) => void;
  /** Fired when a row is double-clicked */
  onRowDoubleClick?: (row: T) => void;

  /** Override the default text-color class per row (overrides disabled text styling) */
  cellTextClass?: (row: T) => string | undefined;

  /** Table & cell classNames */
  tableClassName?: string;
  headCellBaseClass?: string; // applied to all <th>
  bodyCellBaseClass?: string; // applied to all <td>
  checkboxCellClassName?: string; // applied to the selection <td>

  /** Rendered centred in the table body when data is empty */
  emptyComponent?: React.ReactNode;

  /** Pagination */
  showPagination?: boolean;
  totalRecords?: number;
  currentPage?: number;
  limit?: number;
  onPageChange?: (page: number) => void;
};

export function getValue<T>(row: T, accessor: Accessor<T>) {
  if (typeof accessor === 'function') return accessor(row) || '-';
  return (row as any)?.[accessor as keyof T] || '-';
}

// Robust compare for strings / numbers / dates
function compareValues(a: any, b: any, order: SortOrder) {
  const dir = order === 'asc' ? 1 : -1;

  // nullish last
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  // dates
  const aDate = a instanceof Date ? a : new Date(a);
  const bDate = b instanceof Date ? b : new Date(b);
  if (!isNaN(+aDate) && !isNaN(+bDate)) {
    if (+aDate === +bDate) return 0;
    return (+aDate > +bDate ? 1 : -1) * dir;
  }

  // numbers in strings
  const aNum = typeof a === 'string' ? Number(a) : a;
  const bNum = typeof b === 'string' ? Number(b) : b;
  const aIsNum = typeof aNum === 'number' && !isNaN(aNum);
  const bIsNum = typeof bNum === 'number' && !isNaN(bNum);
  if (aIsNum && bIsNum) {
    if (aNum === bNum) return 0;
    return (aNum > bNum ? 1 : -1) * dir;
  }

  // strings
  const A = String(a).toUpperCase();
  const B = String(b).toUpperCase();
  if (A === B) return 0;
  return (A > B ? 1 : -1) * dir;
}

function sortData<T>(
  rows: T[],
  columns: ColumnDef<T>[],
  sortRules: SortRule[],
) {
  if (!sortRules.length) return rows;
  const colMap = new Map(columns.map(c => [c.field, c]));
  const copy = [...rows];

  copy.sort((ra, rb) => {
    for (const rule of sortRules) {
      const col = colMap.get(rule.field);
      if (!col) continue;
      const va = getValue(ra, col.accessor);
      const vb = getValue(rb, col.accessor);
      const res = compareValues(va, vb, rule.order);
      if (res !== 0) return res;
    }
    return 0;
  });

  return copy;
}

function AppTable<T>({
  data,
  columns,
  rowKey,
  selectable,
  selectedKeys = [],
  onSelectionChange,
  rowDisabled,
  searchText = '',
  multiSort = false,
  initialSort = [],
  onSortChange,
  rowClassName,
  onRowClick,
  onRowDoubleClick,
  cellTextClass,
  tableClassName,
  headCellBaseClass = 'border-1 border-gray70 px-2',
  bodyCellBaseClass = 'border-1 border-gray70 p-[10px] align-top',
  checkboxCellClassName = 'border-1 border-gray70 px-[12px] py-[8px] align-top w-[50px]',
  emptyComponent,
  showPagination = false,
  totalRecords = 0,
  currentPage = 0,
  limit = 10,
  onPageChange,
}: ReusableTableProps<T>) {
  const [sort, setSort] = useState<SortRule[]>(initialSort);

  const handleSort = (field: string) => {
    const existing = sort.find(r => r.field === field);

    let next: SortRule[];
    if (!multiSort) {
      // single column: toggle asc <-> desc (no release)
      if (!existing) next = [{field, order: 'asc'}];
      else if (existing.order === 'asc') next = [{field, order: 'desc'}];
      else next = [{field, order: 'asc'}];
    } else {
      // multi: toggle entry in array
      if (!existing) next = [...sort, {field, order: 'asc'}];
      else if (existing.order === 'asc')
        next = sort.map(r => (r.field === field ? {...r, order: 'desc'} : r));
      else next = sort.map(r => (r.field === field ? {...r, order: 'asc'} : r));
    }

    setSort(next);
    onSortChange?.(next);
  };

  const isSorted = (field: string) => sort.some(r => r.field === field);
  const getSortOrder = (field: string): SortOrder | undefined =>
    sort.find(r => r.field === field)?.order;

  const sortedData = useMemo(
    () => sortData(data, columns, sort),
    [data, columns, sort],
  );

  const allKeys = useMemo(() => sortedData.map(rowKey), [sortedData, rowKey]);
  const allChecked =
    selectable &&
    selectedKeys.length > 0 &&
    selectedKeys.length === allKeys.length;

  const someChecked =
    selectable &&
    selectedKeys.length > 0 &&
    selectedKeys.length < allKeys.length;

  const toggleAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) onSelectionChange(allKeys);
    else onSelectionChange([]);
  };

  const toggleOne = (key: string | number) => {
    if (!onSelectionChange) return;
    if (selectedKeys.includes(key))
      onSelectionChange(selectedKeys.filter(k => k !== key));
    else onSelectionChange([...selectedKeys, key]);
  };

  return (
    <div className="flex-1 min-h-0 bg-white pb-4">
      <div className="w-full h-full overflow-auto relative">
        <table
          className={`w-full max-h-full border-collapse ${
            sortedData.length > 0 ? 'border-2 border-gray70' : ''
          } ${tableClassName ?? ''}`}>
          <thead>
            <tr>
              {selectable && (
                <th className="sticky top-0 z-[1] border-1 border-gray70 px-1 bg-gray95 w-[50px]">
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={!!allChecked}
                    indeterminate={!!someChecked}
                    onChange={e => toggleAll(e.target.checked)}
                  />
                </th>
              )}

              {columns.map(col => {
                const sorted = isSorted(col.field);
                const order = getSortOrder(col.field);
                return (
                  <th
                    key={col.field}
                    className={`${headCellBaseClass} sticky top-0 z-[1] transition-colors duration-200 ${
                      sorted ? 'bg-lightBlueGray' : 'bg-gray95'
                    } ${col.headerClassName ?? ''}`}
                    style={
                      col.width
                        ? {width: `${col.width}px`, maxWidth: `${col.width}px`}
                        : undefined
                    }>
                    {col.sortable ? (
                      <button
                        className="flex items-center gap-3"
                        onClick={() => handleSort(col.field)}>
                        <span>{col.title}</span>
                        <img
                          src={
                            sorted
                              ? SVG.downArrowBlackIcon
                              : SVG.downArrowGrayIcon
                          }
                          alt="sort"
                          className={`w-[16px] h-[16px] object-contain ${
                            order === 'desc' ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span>{col.title}</span>
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {sortedData.length === 0 && emptyComponent && (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)}>
                  {emptyComponent}
                </td>
              </tr>
            )}
            {sortedData.map(row => {
              const key = rowKey(row);
              const disabled = rowDisabled?.(row) ?? false;
              const isSelected = selectedKeys.includes(key);

              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onDoubleClick={
                    onRowDoubleClick ? () => onRowDoubleClick(row) : undefined
                  }
                  className={`transition-all duration-200 ${
                    onRowClick || onRowDoubleClick ? 'cursor-pointer' : ''
                  } ${
                    isSelected
                      ? 'bg-lightIceBlue hover:shadow-bottom-sm'
                      : disabled
                        ? 'bg-gray98'
                        : 'hover:bg-lightIceBlue hover:shadow-bottom-sm'
                  } ${rowClassName?.(row) ?? ''}`}>
                  {selectable && (
                    <td
                      className={checkboxCellClassName}
                      onClick={e => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        color="secondary"
                        checked={isSelected}
                        sx={{p: 0, m: 0}}
                        onChange={() => toggleOne(key)}
                      />
                    </td>
                  )}

                  {columns.map(col => {
                    const content = col.render
                      ? col.render(row)
                      : col.searchable === true
                        ? (highlightSearchMatch(
                            String(getValue(row, col.accessor)),
                            searchText,
                          ) ?? '')
                        : String(getValue(row, col.accessor));
                    const cellClasses =
                      typeof col.cellClassName === 'function'
                        ? col.cellClassName(row)
                        : (col.cellClassName ?? '');

                    return (
                      <td
                        key={col.field}
                        className={`${bodyCellBaseClass} ${
                          cellTextClass?.(row) ??
                          (disabled ? 'text-gray' : 'text-black')
                        } ${cellClasses}`}
                        style={
                          col.width
                            ? {
                                width: `${col.width}px`,
                                maxWidth: `${col.width}px`,
                              }
                            : undefined
                        }>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        {showPagination && (
          <div className="sticky bottom-0 left-0 right-0 bg-white">
            <TablePaginationActions
              count={totalRecords}
              rowsPerPage={limit}
              page={currentPage}
              onPageChange={onPageChange!}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default AppTable;
